import { StatusBar } from "@-/com.npmjs.expo-status-bar";
import { ScopedVariables } from "@-/com.npmjs.uniwind";
import type { PropsWithChildren } from "react";
import {
  SPACE_CHROME_COLOR,
  SPACE_CHROME_COLOR_VARIABLE,
  SPACE_SKY_FOREGROUND_CHANNELS,
} from "#space-backdrop-palette";
import { useIsSpaceBackgroundEnabled } from "#use-is-space-background-enabled";

const NO_VARIABLES: Record<string, string> = {};
const SPACE_VARIABLES: Record<string, string> = {
  [SPACE_CHROME_COLOR_VARIABLE]: SPACE_CHROME_COLOR,
};
/** Both the raw channel token (read by the inlined utilities) and its `--color-*` alias. */
const SKY_FOREGROUND_VARIABLES: Record<string, string> = Object.fromEntries(
  Object.entries(SPACE_SKY_FOREGROUND_CHANNELS).flatMap(([token, channels]) => [
    [`--${token}`, channels],
    [`--color-${token}`, `hsl(${channels})`],
  ]),
);

/**
 * Defines `--color-space-chrome` for everything beneath it while the space background is on. The
 * scope stays mounted when it is off (with no variables), so flipping the setting restyles the
 * app in place instead of remounting the navigators under it.
 */
export function SpaceThemeScope({ children }: PropsWithChildren) {
  const isSpaceBackgroundEnabled = useIsSpaceBackgroundEnabled();
  return (
    <ScopedVariables variables={isSpaceBackgroundEnabled ? SPACE_VARIABLES : NO_VARIABLES}>
      {children}
    </ScopedVariables>
  );
}

/** Light status-bar content over the sky. Mount it only where the status band shows the sky. */
export function SpaceStatusBar() {
  return useIsSpaceBackgroundEnabled() ? <StatusBar style="light" /> : null;
}

/**
 * Light ink for content drawn straight onto the sky (only icons and labels: anything with its own
 * surface beneath this scope would get light ink too). Stays mounted while `enabled` is false so
 * toggling never remounts the subtree.
 */
export function SpaceSkyForegroundScope({
  children,
  enabled,
}: PropsWithChildren<{ enabled: boolean }>) {
  return (
    <ScopedVariables variables={enabled ? SKY_FOREGROUND_VARIABLES : NO_VARIABLES}>
      {children}
    </ScopedVariables>
  );
}
